import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Swal from "sweetalert2";
import AdminLayout from "@/components/feature/AdminLayout";
import LoadingState from "@/components/base/LoadingState";
import ErrorState from "@/components/base/ErrorState";
import { requireAuth } from "@/middleware/authGuard";
import { supabase } from "@/lib/supabaseClient";
import { getTenantId } from "@/lib/getTenant";
import OverviewTab  from "./tabs/OverviewTab";
import PhotosTab    from "./tabs/PhotosTab";
import MessagesTab  from "./tabs/MessagesTab";
import QRTab        from "./tabs/QRTab";
import PortalTab    from "./tabs/PortalTab";
import { getExpiryStatus, getExpiryBadge, activateSignatureBook, renewSignatureBook } from "@/lib/signatureBookExpiry";

interface SignatureBook {
  id: string;
  tenant_id: string;
  title: string;
  status: string;
  event_date: string | null;
  guest_book_enabled: boolean;
  closing_message: string | null;
  activated_at: string | null;
  expires_at: string | null;
  client_id: string | null;
  contract_id: string | null;
  job_id: string | null;
  public_slug: string | null;
  cover_photo_url: string | null;
  qr_overlay_logo_url: string | null;
  created_at: string;
  updated_at: string;
}

type TabId = "overview" | "photos" | "messages" | "qr" | "portal";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "overview",  label: "Overview",    icon: "ri-information-line" },
  { id: "photos",    label: "Fotos",       icon: "ri-image-2-line" },
  { id: "messages",  label: "Mensajes",    icon: "ri-message-3-line" },
  { id: "qr",        label: "Diseño / QR", icon: "ri-qr-code-line" },
  { id: "portal",    label: "Portal / PDF", icon: "ri-share-line" },
];

const STATUS_COLORS: Record<string, string> = {
  draft:     "bg-slate-100 text-slate-500",
  published: "bg-emerald-100 text-emerald-700",
  archived:  "bg-orange-100 text-orange-600",
};
const STATUS_LABELS: Record<string, string> = {
  draft: "Borrador", published: "Publicado", archived: "Archivado",
};

export default function SignatureBookDetailPage() {
  const { bookId }  = useParams<{ bookId: string }>();
  const navigate    = useNavigate();

  const [tenantId,   setTenantId]   = useState<string | null>(null);
  const [book,       setBook]       = useState<SignatureBook | null>(null);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState<string | null>(null);
  const [activeTab,  setActiveTab]  = useState<TabId>("overview");
  const [dirty,      setDirty]      = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [activating, setActivating] = useState(false);

  const onChange = useCallback(<K extends keyof SignatureBook>(k: K, v: SignatureBook[K]) => {
    setBook((prev) => prev ? { ...prev, [k]: v } : prev);
    setDirty(true);
  }, []);

  const fetchBook = useCallback(async (tid: string) => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await supabase
      .from("signature_books")
      .select("*")
      .eq("id", bookId)
      .eq("tenant_id", tid)
      .maybeSingle();
    setLoading(false);
    if (err)   { setError(err.message); return; }
    if (!data) { setError("Libro no encontrado."); return; }
    setBook(data as SignatureBook);
  }, [bookId]);

  useEffect(() => {
    const init = async () => {
      const ok = await requireAuth();
      if (!ok) { navigate("/login"); return; }
      const tid = await getTenantId();
      setTenantId(tid);
      if (tid && bookId) fetchBook(tid);
      else setLoading(false);
    };
    init();
  }, [navigate, fetchBook, bookId]);

  const handleActivate = async (mode: "activate" | "renew") => {
    if (!tenantId || !book) return;
    setActivating(true);
    try {
      const updated = mode === "activate"
        ? await activateSignatureBook(book.id, tenantId)
        : await renewSignatureBook(book.id, tenantId, book.expires_at);
      setBook((prev) => prev ? { ...prev, ...updated } : prev);
      Swal.fire({ title: mode === "activate" ? "Libro activado" : "Libro renovado", text: `Vence el ${new Date(updated.expires_at).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}`, icon: "success", timer: 2200, showConfirmButton: false });
    } catch (e) {
      Swal.fire({ title: "Error", text: e instanceof Error ? e.message : "Error desconocido", icon: "error" });
    } finally {
      setActivating(false);
    }
  };

  const handleSave = async () => {
    if (!tenantId || !book) return;
    if (!book.title.trim()) {
      Swal.fire({ title: "Falta el título", icon: "warning" }); return;
    }
    setSaving(true);
    const { data, error: err } = await supabase
      .from("signature_books")
      .update({
        title:              book.title,
        status:             book.status,
        client_id:          book.client_id,
        contract_id:        book.contract_id,
        job_id:             book.job_id,
        event_date:         book.event_date,
        guest_book_enabled: book.guest_book_enabled,
        closing_message:    book.closing_message,
        activated_at:       book.activated_at,
        expires_at:         book.expires_at,
        updated_at:         new Date().toISOString(),
      })
      .eq("id", book.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();
    setSaving(false);
    if (err) { Swal.fire({ title: "Error", text: err.message, icon: "error" }); return; }
    if (data) { setBook(data as SignatureBook); setDirty(false); }
    Swal.fire({ title: "Guardado", icon: "success", timer: 1400, showConfirmButton: false });
  };

  return (
    <AdminLayout>
      {loading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState message={error} onRetry={() => tenantId && fetchBook(tenantId)} showLogin />
      ) : !book ? null : (
        <div className="min-h-screen bg-slate-50">

          {/* Top bar */}
          <div className="bg-white border-b border-slate-200 px-4 md:px-8 py-4">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3 min-w-0">
                <Link
                  to="/admin/signature-books"
                  className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
                  Libros de Firmas
                </Link>
                <span className="text-slate-200">/</span>
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 shrink-0">
                    <i className="ri-book-open-line text-sm"></i>
                  </div>
                  <div className="min-w-0">
                    <p className="text-slate-800 font-semibold text-sm truncate">{book.title}</p>
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[book.status] ?? "bg-slate-100 text-slate-500"}`}>
                        {STATUS_LABELS[book.status] ?? book.status}
                      </span>
                      {book.event_date && (
                        <span className="text-slate-400 text-xs">
                          {new Date(book.event_date).toLocaleDateString("es-MX", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                {/* Expiry badge */}
                {(() => {
                  const expStatus = getExpiryStatus(book.expires_at);
                  const badge     = getExpiryBadge(expStatus);
                  return (
                    <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${badge.label ? badge.className : "bg-slate-100 text-slate-400"}`}>
                      <span className="w-3 h-3 flex items-center justify-center">
                        <i className={`${badge.icon || "ri-time-line"} text-[10px]`}></i>
                      </span>
                      {badge.label || "Sin activar"}
                    </span>
                  );
                })()}
                {/* Activate / Renew button */}
                {(() => {
                  const expStatus = getExpiryStatus(book.expires_at);
                  if (expStatus === "none" || expStatus === "expired") {
                    return (
                      <button
                        onClick={() => handleActivate(expStatus === "none" ? "activate" : "renew")}
                        disabled={activating}
                        className="flex items-center gap-1.5 border border-emerald-300 text-emerald-600 hover:bg-emerald-50 text-xs font-semibold px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {activating
                          ? <div className="w-3.5 h-3.5 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
                          : <i className="ri-play-circle-line"></i>}
                        {expStatus === "none" ? "Activar" : "Renovar (3 meses)"}
                      </button>
                    );
                  }
                  if (expStatus === "soon") {
                    return (
                      <button
                        onClick={() => handleActivate("renew")}
                        disabled={activating}
                        className="flex items-center gap-1.5 border border-amber-300 text-amber-600 hover:bg-amber-50 text-xs font-semibold px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                      >
                        {activating
                          ? <div className="w-3.5 h-3.5 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                          : <i className="ri-refresh-line"></i>}
                        Renovar
                      </button>
                    );
                  }
                  return null;
                })()}
                {dirty && (
                  <span className="text-amber-500 text-xs flex items-center gap-1">
                    <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-circle-fill text-[6px]"></i></span>
                    Sin guardar
                  </span>
                )}
                {book.public_slug && book.status === "published" && (
                  <a
                    href={`/guestbook/${book.public_slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 border border-slate-200 text-slate-500 hover:text-slate-700 text-sm px-3 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-external-link-line"></i></span>
                    Ver libro
                  </a>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <i className="ri-save-line"></i>}
                  {saving ? "Guardando…" : "Guardar"}
                </button>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white border-b border-slate-200 px-4 md:px-8">
            <div className="flex items-center gap-0 overflow-x-auto">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3.5 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap -mb-px ${
                    activeTab === tab.id
                      ? "border-amber-500 text-amber-600"
                      : "border-transparent text-slate-400 hover:text-slate-600 hover:border-slate-200"
                  }`}
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className={`${tab.icon} text-sm`}></i></span>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="px-4 md:px-8 py-6">
            {activeTab === "overview" && tenantId && (
              <OverviewTab
                book={book}
                tenantId={tenantId}
                onChange={onChange}
                onSave={handleSave}
                saving={saving}
              />
            )}
            {activeTab === "photos" && tenantId && (
              <PhotosTab
                bookId={book.id}
                tenantId={tenantId}
                coverPhotoUrl={book.cover_photo_url}
                onCoverChange={(url) => setBook((prev) => prev ? { ...prev, cover_photo_url: url } : prev)}
              />
            )}
            {activeTab === "messages" && tenantId && (
              <MessagesTab bookId={book.id} tenantId={tenantId} />
            )}
            {activeTab === "qr" && (
              <QRTab
                bookId={book.id}
                publicSlug={book.public_slug}
                qrOverlayLogoUrl={book.qr_overlay_logo_url}
                onLogoChange={(url) => setBook((prev) => prev ? { ...prev, qr_overlay_logo_url: url } : prev)}
                tenantId={tenantId ?? undefined}
              />
            )}
            {activeTab === "portal" && (
              <PortalTab publicSlug={book.public_slug} status={book.status} />
            )}
          </div>

          {/* Sticky save bar */}
          {dirty && (
            <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40">
              <div className="flex items-center gap-3 bg-[#0f172a] text-white px-5 py-3 rounded-2xl shadow-lg">
                <span className="w-4 h-4 flex items-center justify-center text-amber-400">
                  <i className="ri-circle-fill text-[6px]"></i>
                </span>
                <p className="text-sm">Cambios sin guardar</p>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white text-xs font-semibold px-4 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {saving ? "Guardando…" : "Guardar ahora"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </AdminLayout>
  );
}